import {
  ErrorData,
  ErrorInfo,
  FieldData,
  FormContent,
  GenericData,
} from "../types";
import { getValidationFunction } from "../validation";

export type Action<T> =
  | {
      type: "INITIALIZE_VALIDATION";
      payload: {
        fieldDatas: FieldData[];
        populatedData: T;
        isNew?: boolean;
      };
    }
  | {
      type: "REFRESH_VALIDATION";
      payload: {
        tempData: T;
        isNew?: boolean;
      };
    };

export const validationReducer = <T extends FormContent>(
  state: ErrorData[],
  action: Action<T>
) => {
  switch (action.type) {
    case "INITIALIZE_VALIDATION":
      const validatedFields = action.payload.fieldDatas.filter(
        (a) => a.validations && a.validations.length > 0
      );

      if (!Array.isArray(action.payload.populatedData.values)) {
        const nonLocalizedState = validatedFields.map((field) => {
          let fieldValue = (action.payload.populatedData as GenericData)[
            field.alias
          ];
          const errorInfos: ErrorInfo[] = [];
          if (field.validations) {
            field.validations.forEach((validation) => {
              const targetFieldName = Object.values(validation)[0];
              const targetFieldValue = (
                action.payload.populatedData as GenericData
              )[targetFieldName];

              const functionKey = Object.keys(validation)[0];
              let args: any | any[] = [];
              switch (functionKey) {
                case "isRequiredIf":
                case "isBefore":
                case "isSameOrBefore":
                case "isAfter":
                case "isSameOrAfter":
                  args = [Object.values(validation)[1], targetFieldValue];
                  break;
                case "isDisabledIfNew":
                  fieldValue = action.payload.isNew;
                  args = Object.values(validation)[0];
                  break;
                case "isNotInFuture":
                case "isNonNegative":
                case "isNotNullOrEmpty":
                  args = typeof fieldValue as any;
                  break;
                case "isNotOneOf":
                  args = Object.values(validation)[0];
                  break;
                default:
                  args = Object.values(validation);
                  break;
              }
              const validationFunction = getValidationFunction(functionKey);
              const result =
                validationFunction && validationFunction(fieldValue, args);
              if (result) {
                errorInfos.push(result);
              }
            });
          }
          const errorData: ErrorData = {
            alias: field.alias,
            type: field.type,
            validations: field.validations,
            errors: errorInfos,
          };
          return errorData;
        });
        const initialState: ErrorData[] = [...nonLocalizedState];
        return initialState;
      } else {
        const nonLocalizedState = validatedFields.map((field) => {
          let fieldValue = action.payload.populatedData.values?.find(
            (a) => a.fieldAlias === field.alias
          )?.value;
          const errorInfos: ErrorInfo[] = [];
          if (field.validations) {
            field.validations.forEach((validation) => {
              const targetFieldName = Object.values(validation)[0];
              let targetFieldValue = action.payload.populatedData.values?.find(
                (a) => a.fieldAlias === targetFieldName
              )?.value;
              const functionKey = Object.keys(validation)[0];
              let args: any | any[] = [];
              switch (functionKey) {
                case "isRequiredIf":
                case "isBefore":
                case "isSameOrBefore":
                case "isAfter":
                case "isSameOrAfter":
                  args = [Object.values(validation)[1], targetFieldValue];
                  break;
                case "isDisabledIfNew":
                  fieldValue = action.payload.isNew;
                  args = Object.values(validation)[0];
                  break;
                case "isNotInFuture":
                case "isNonNegative":
                case "isNotNullOrEmpty":
                  args = typeof fieldValue as any;
                  break;
                case "isNotOneOf":
                  args = Object.values(validation)[0];
                  break;
                default:
                  args = Object.values(validation);
                  break;
              }
              const validationFunction = getValidationFunction(functionKey);
              const result =
                validationFunction && validationFunction(fieldValue, args);
              if (result) {
                errorInfos.push(result);
              }
            });
          }
          const errorData: ErrorData = {
            alias: field.alias,
            type: field.type,
            validations: field.validations,
            errors: errorInfos,
          };
          return errorData;
        });
        const initialState: ErrorData[] = [...nonLocalizedState];
        return initialState;
      }
    case "REFRESH_VALIDATION":
      if (!Array.isArray(action.payload.tempData.values)) {
        const refreshedState = state.map((item) => {
          let errorInfos: ErrorInfo[] = [];
          let fieldValue = (action.payload.tempData as GenericData)[item.alias];
          if (item.validations) {
            item.validations.forEach((validation) => {
              const functionKey = Object.keys(validation)[0];
              let args: any | any[] = [];
              switch (functionKey) {
                case "isRequiredIf":
                case "isBefore":
                case "isSameOrBefore":
                case "isAfter":
                case "isSameOrAfter":
                  const targetFieldName = Object.values(validation)[0];
                  const targetFieldValue = (
                    action.payload.tempData as GenericData
                  )[targetFieldName];
                  args = [Object.values(validation)[1], targetFieldValue];
                  break;
                case "isDisabledIfNew":
                  fieldValue = action.payload.isNew;
                  args = Object.values(validation)[0];
                  break;
                case "isNotInFuture":
                case "isNonNegative":
                case "isNotNullOrEmpty":
                  args = typeof fieldValue as any;
                  break;
                case "isNotOneOf":
                  args = Object.values(validation)[0];
                  break;
                default:
                  args = [Object.values(validation)];
                  break;
              }
              const validationFunction = getValidationFunction(functionKey);
              const result = validationFunction(fieldValue, args);
              if (result) {
                errorInfos.push(result);
              } else {
                errorInfos = errorInfos.filter((a) => a.error !== functionKey);
              }
            });
          }
          return { ...item, errors: errorInfos };
        });
        return refreshedState;
      } else {
        const refreshedState = state.map((item) => {
          let errorInfos: ErrorInfo[] = [];
          let fieldValue = action.payload.tempData.values.find(
            (a) => a.fieldAlias === item.alias
          )?.value;
          if (item.validations) {
            item.validations.forEach((validation) => {
              const functionKey = Object.keys(validation)[0];
              let args: any | any[] = [];
              switch (functionKey) {
                case "isRequiredIf":
                case "isBefore":
                case "isSameOrBefore":
                case "isAfter":
                case "isSameOrAfter":
                  const targetFieldName = Object.values(validation)[0];
                  const targetFieldValue = action.payload.tempData.values.find(
                    (a) => a.fieldAlias === targetFieldName
                  )?.value;
                  args = [Object.values(validation)[1], targetFieldValue];
                  break;
                case "isDisabledIfNew":
                  fieldValue = action.payload.isNew;
                  args = Object.values(validation)[0];
                  break;
                case "isNotInFuture":
                case "isNonNegative":
                case "isNotNullOrEmpty":
                  args = typeof fieldValue as any;
                  break;
                case "isNotOneOf":
                  args = Object.values(validation)[0];
                  break;
                default:
                  args = [Object.values(validation)];
                  break;
              }
              const validationFunction = getValidationFunction(functionKey);
              const result = validationFunction(fieldValue, args);
              if (result) {
                errorInfos.push(result);
              } else {
                errorInfos = errorInfos.filter((a) => a.error !== functionKey);
              }
            });
          }
          return { ...item, errors: errorInfos };
        });
        return refreshedState;
      }
    default:
      return state;
  }
};
